import { Link } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { Pill, TableSkeleton } from "../components/Pill";
import {
  COMPLETENESS_STATUS_UI,
  COMPLETENESS_VERDICT_UI,
} from "../lib/enumUI";
import { DiscrepancyList } from "./detail/DiscrepancyList";
import { ValidationPanel } from "./detail/ValidationPanel";
import { projectDetailQueryOptions, requestProjectRescan } from "./queries";
import type { ProjectRescanResponse, RequirementEvaluation } from "./types";

export function ProjectDetail({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery(
    projectDetailQueryOptions(projectId),
  );

  const rescanMutation = useMutation({
    mutationFn: () => requestProjectRescan(projectId),
    onSuccess: () => {
      // Folder rescan may land new observed-file rows; re-fetch detail
      // and timeline so counts and history reflect what the pass did.
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "detail"],
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "timeline"],
      });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  if (isLoading) {
    return (
      <div className="page">
        <TableSkeleton />
      </div>
    );
  }
  if (error) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <div className="page">
        <p className="alert" role="alert">
          Failed to load project: {message}
        </p>
      </div>
    );
  }
  if (!data) return null;

  const { completeness } = data;

  return (
    <div className="page">
      <p className="page-breadcrumb">
        <Link to="/projects">← All projects</Link>
        <span className="sep">·</span>
        <Link to="/projects/$projectId/timeline" params={{ projectId }}>
          Timeline
        </Link>
      </p>
      <h1>Project {data.display_reference}</h1>
      <p className="page-subline">
        <span>Status:</span>
        <Pill tone={COMPLETENESS_STATUS_UI[data.completeness_status].tone}>
          {COMPLETENESS_STATUS_UI[data.completeness_status].label}
        </Pill>
      </p>

      <RescanAction
        onRescan={() => rescanMutation.mutate()}
        isPending={rescanMutation.isPending}
        error={rescanMutation.error}
        lastResult={rescanMutation.data}
      />

      <h2>Required documents</h2>
      {completeness.requirement_evaluations.length === 0 ? (
        <p className="empty-state">
          No requirements defined for this project's lifecycle stage.
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Document role</th>
              <th>Verdict</th>
              <th>Shortfall</th>
              <th>Satisfying</th>
              <th>In review</th>
              <th>Rationale</th>
            </tr>
          </thead>
          <tbody>
            {completeness.requirement_evaluations.map((req) => (
              <RequirementRow key={req.requirement_id} requirement={req} />
            ))}
          </tbody>
        </table>
      )}

      <h2>Unmatched documents</h2>
      {completeness.unmatched_documents.length === 0 ? (
        <p className="empty-state">None.</p>
      ) : (
        <ul>
          {completeness.unmatched_documents.map((id) => (
            <li key={id}>
              <code>{id}</code>
            </li>
          ))}
        </ul>
      )}

      <ValidationPanel results={data.cross_checks} />
      <DiscrepancyList items={data.discrepancies} />
    </div>
  );
}

function RescanAction({
  onRescan,
  isPending,
  error,
  lastResult,
}: {
  onRescan: () => void;
  isPending: boolean;
  error: unknown;
  lastResult: ProjectRescanResponse | undefined;
}) {
  const summary = lastResult ? summarizeRescan(lastResult) : null;
  return (
    <p>
      <button
        type="button"
        className="btn"
        onClick={onRescan}
        disabled={isPending}
      >
        {isPending ? "Scanning folders…" : "Re-scan folder"}
      </button>
      {error ? (
        <span className="inline-status inline-status--error" role="alert">
          failed: {error instanceof Error ? error.message : String(error)}
        </span>
      ) : null}
      {summary && !isPending && !error ? (
        <span className="inline-status">{summary}</span>
      ) : null}
    </p>
  );
}

function summarizeRescan(result: ProjectRescanResponse): string {
  if (result.outcomes.length === 0) {
    return "no folders bound to this project";
  }
  const totals = result.outcomes.reduce(
    (acc, o) => ({
      new_files: acc.new_files + o.new_files,
      new_versions: acc.new_versions + o.new_versions,
      touched: acc.touched + o.touched,
      unreachable: acc.unreachable + (o.unreachable ? 1 : 0),
    }),
    { new_files: 0, new_versions: 0, touched: 0, unreachable: 0 },
  );
  const parts = [
    `${result.outcomes.length} folder${result.outcomes.length === 1 ? "" : "s"}`,
    `${totals.new_files} new`,
    `${totals.new_versions} versioned`,
    `${totals.touched} touched`,
  ];
  if (totals.unreachable > 0) {
    parts.push(`${totals.unreachable} unreachable`);
  }
  return parts.join(" · ");
}

function RequirementRow({
  requirement,
}: {
  requirement: RequirementEvaluation;
}) {
  return (
    <tr>
      <td>{requirement.document_role}</td>
      <td>
        <Pill tone={COMPLETENESS_VERDICT_UI[requirement.verdict].tone}>
          {COMPLETENESS_VERDICT_UI[requirement.verdict].label}
        </Pill>
      </td>
      <td>{requirement.shortfall}</td>
      <td>{requirement.satisfying_documents.length}</td>
      <td>{requirement.review_documents.length}</td>
      <td>{requirement.rationale}</td>
    </tr>
  );
}
